<script setup>
import { ref, watch, computed } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { useRequest } from '@/composables/useRequest';
import { useMediaQuery } from '@vueuse/core';
import { createSearchSchema } from '@/configs/validation-schemas';

import { useRouter, useRoute } from '#app';

const props = defineProps({
  searchQuery: {
    type: String,
    default: '',
  },
  totalFound: {
    type: Number,
    default: 0,
  },
  results: {
    type: Array,
    default: () => [],
  },
  view: {
    type: String,
    default: '',
  },
});

const router = useRouter();
const route = useRoute();
const langPrefix = route.params.lang ? `/${route.params.lang}` : '';

const { t } = useI18n();
const isTablet = useMediaQuery('(min-width: 768px)');
const loading = ref(false);
const hasSearched = ref(!!props.searchQuery);
const localTotalFound = ref(props.totalFound);
const localResults = ref(props.results);

const searchSchema = computed(() => createSearchSchema(t));

const { handleSubmit, resetForm } = useForm({
  validationSchema: searchSchema,
  initialValues: {
    s: props.searchQuery || '',
  },
});

const { value: searchQueryValue, errorMessage } = useField('s');

watch(
  () => props.searchQuery,
  (newVal) => {
    searchQueryValue.value = newVal || '';
  },
);
watch(
  () => props.totalFound,
  (newVal) => {
    localTotalFound.value = newVal;
  },
);
watch(
  () => props.results,
  (newVal) => {
    localResults.value = newVal;
  },
  { deep: true },
);

const clearSearchQuery = () => {
  resetForm({ values: { s: '' } });
  localTotalFound.value = 0;
  localResults.value = [];
  hasSearched.value = false;
};

const doSearch = async (query) => {
  loading.value = true;
  try {
    const response = await useRequest(`search?q=${encodeURIComponent(query)}`);
    localTotalFound.value = response?.total_found || 0;
    localResults.value = response?.results
      ? Object.values(response.results)
      : [];
    if (props.view !== 'header') {
      router.push({
        query: {
          ...route.query,
          s: query || undefined,
        },
      });
    }
  } catch (error) {
    console.error('[Search Error]:', error);
    localTotalFound.value = 0;
    localResults.value = [];
  } finally {
    loading.value = false;
    hasSearched.value = true;
  }
};

const onSubmit = handleSubmit((values) => {
  const trimmed = values.s.trim();
  if (trimmed.length >= 3) {
    doSearch(trimmed);
  }
});
</script>

<template>
  <div :class="['search', `search--${view}`]">
    <form class="search__form" @submit.prevent="onSubmit">
      <InputGroup
        class="search__field"
        :class="[
          searchQueryValue ? 'search__field--with-value' : '',
          errorMessage ? 'search__field--error' : '',
        ]"
      >
        <InputGroupAddon class="search__icon">
          <CIcon
            name="search-black"
            width="16"
            height="16"
            class="search__icon-el"
          />
        </InputGroupAddon>

        <InputText
          v-model="searchQueryValue"
          type="text"
          size="small"
          :placeholder="$t('search.field_placeholder')"
          :invalid="!!errorMessage"
          enter-key-hint="search"
          inputmode="search"
          class="search__input search-input"
        />

        <InputGroupAddon
          v-if="searchQueryValue"
          class="search__clear"
          @click="clearSearchQuery"
        >
          <CIcon
            class="search__clear-icon"
            name="close-black"
            width="16"
            height="16"
          />
        </InputGroupAddon>
        <Button
          :label="$t('search.search_button')"
          type="submit"
          class="search__button"
          variant="text"
          size="small"
        />
      </InputGroup>

      <transition name="fade">
        <small v-if="errorMessage" class="search__error-text">
          {{ errorMessage }}
        </small>
      </transition>
    </form>

    <div v-if="loading" class="search__loading-status text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
    </div>
    <template v-else>
      <div v-if="hasSearched && localTotalFound > 0" class="search__results">
        <ScrollPanel v-if="view === 'header'" class="search__results-panel">
          <div class="search__results-wrap">
            <div class="search__results-total body-3-2-bold">
              <span>{{ $t('search.founded') }} </span>
              <span
                class="search__results-total-value"
                v-html="localTotalFound"
              ></span>
            </div>
            <ul class="m-0 p-0 search__results-categories">
              <li
                v-for="(category, categoryIndex) in localResults"
                :key="`search-${categoryIndex}`"
                class="m-0 search__results-category"
              >
                <p
                  class="m-0 body-3-medium search__results-category-title"
                  v-html="category.title"
                ></p>
                <ul class="m-0 p-0 search__results-items">
                  <li
                    v-for="(item, itemIndex) in category.items"
                    :key="`search-${itemIndex}`"
                    class="m-0 search__results-item"
                  >
                    <NuxtLink
                      :to="item.url"
                      class="search__results-item-link body-2-regular"
                    >
                      <span v-html="item.title"></span>
                    </NuxtLink>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
          <div class="search__results-bottom">
            <Button v-slot="slotProps" as-child variant="text">
              <CTagDetect
                :to="`${langPrefix}/search?s=${searchQueryValue}`"
                :class="[
                  slotProps.class,
                  'search__results-button button-icon-next',
                ]"
              >
                <span
                  >{{ $t('search.all_results') }} ({{ localTotalFound }})</span
                >
                <CIcon
                  class="button-icon-prev-icon"
                  name="arrow-red"
                  width="16"
                  height="16"
                />
              </CTagDetect>
            </Button>
          </div>
        </ScrollPanel>

        <template v-else>
          <div class="search__results-total body-3-2-bold">
            <span>{{ $t('search.founded') }} </span>
            <span
              class="search__results-total-value"
              v-html="localTotalFound"
            ></span>
          </div>
          <ul class="m-0 p-0 search__results-categories">
            <li
              v-for="(category, categoryIndex) in localResults"
              :key="`search-${categoryIndex}`"
              class="m-0 search__results-category"
            >
              <p
                class="m-0 body-3-medium search__results-category-title"
                v-html="category.title"
              ></p>
              <ul class="m-0 p-0 search__results-items">
                <li
                  v-for="(item, itemIndex) in category.items"
                  :key="`search-${itemIndex}`"
                  class="m-0 search__results-item"
                >
                  <NuxtLink
                    :to="item.url"
                    class="search__results-item-link body-2-regular"
                  >
                    <span v-html="item.title"></span>
                  </NuxtLink>
                </li>
              </ul>
            </li>
          </ul>
        </template>
      </div>
      <div
        v-else-if="hasSearched && localTotalFound === 0"
        class="search__empty"
      >
        <div class="search__results-total body-3-2-bold">
          <span>{{ $t('search.founded') }} </span>
          <span class="search__results-total-value">0</span>
        </div>
        <div class="search__empty-content border-top pt-4">
          <p
            class="body-3-bold text-muted mb-2"
            v-html="$t('search.results_empty_title', { word: searchQuery })"
          ></p>
          <div v-html="$t('search.results_empty_text')"></div>
        </div>
      </div>
    </template>
  </div>
</template>
<style lang="scss">
.search {
  $parent: &;
  display: grid;
  margin: 0 auto;
  &--page {
    gap: 40px;
    @include media-breakpoint-up(sm) {
      gap: 64px;
      max-width: 784px;
      padding: 0 62px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 64);
      @include responsive-spacing(max-width, 784);
      @include responsive-spacing(padding-right, 62);
      @include responsive-spacing(padding-left, 62);
    }
  }
  &--header {
    gap: 16px;
    @include media-breakpoint-up(md) {
      max-width: 784px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
      @include responsive-spacing(max-width, 784);
    }
    #{$parent} {
      &__empty {
        background-color: $white;
        border-radius: 8px;
        padding: 16px 24px 8px 16px;
        position: relative;
        @include media-breakpoint-up(sm) {
          padding: 24px 24px 8px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-radius, 8);
          @include responsive-spacing(padding, 24);
          @include responsive-spacing(padding-bottom, 8);
        }
      }
      &__results {
        background-color: $white;
        border-radius: 8px;
        padding: 16px 24px 8px 16px;
        position: relative;
        @include media-breakpoint-up(sm) {
          padding: 24px 24px 8px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-radius, 8);
          @include responsive-spacing(padding, 24);
          @include responsive-spacing(padding-bottom, 8);
        }
        &-total {
          background-color: $white;
          position: sticky;
          top: 0;
        }
      }
    }
  }
  &__form {
    background-color: $white;
    border-radius: 8px;
    padding: 16px;
    position: relative;
    @include media-breakpoint-up(sm) {
      padding: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
      @include responsive-spacing(padding, 24);
    }
  }
  &__field {
    &.p-inputgroup {
      align-items: center;
      display: grid;
      grid-template-columns: 16px auto 16px min-content;
      gap: 12px;
      border-bottom: 1px solid $black;
      grid-template-areas: 'search-icon search-input search-input search-button';
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-bottom-width, 1);
        @include responsive-spacing(gap, 12);
        grid-template-columns: px-to-vw(16) auto px-to-vw(16) min-content;
      }
    }
    &--with-value {
      &.p-inputgroup {
        grid-template-areas: 'search-icon search-input search-clear search-button';
        #{$parent} {
          &__clear {
            display: block;
          }
        }
      }
    }
  }
  &__icon {
    border: unset;
    grid-area: search-icon;
    margin: 0;
    min-width: unset;
    padding: 0;
    position: relative;
    &-el {
      height: 100%;
      width: 100%;
    }
  }
  &__input {
    border-end-end-radius: 0;
    border-start-end-radius: 0;
    border-radius: 0;
    grid-area: search-input;
    width: 100%;
  }
  &__clear {
    cursor: pointer;
    display: none;
    grid-area: search-clear;
    margin: 0;
    min-width: unset;
    padding: 0;
    position: relative;
    &-icon {
      height: 16px;
      width: 16px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(height, 16);
        @include responsive-spacing(width, 16);
      }
    }
  }
  &__button {
    grid-area: search-button;
  }
  &__results {
    display: grid;
    gap: 24px;
    @include media-breakpoint-up(sm) {
      gap: 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 40);
    }
    &-panel {
      max-height: calc(100vh - 152px);
      @include media-breakpoint-up(sm) {
        max-height: calc(100vh - 192px);
      }
      @include media-breakpoint-up(md) {
        max-height: calc(100vh - px-to-vw(192));
      }
    }
    &-wrap {
      display: grid;
      gap: 24px;
      @include media-breakpoint-up(sm) {
        gap: 40px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 40);
      }
    }
    &-total {
      color: $on-surface-variant-2;
      display: flex;
      gap: 4px;
      flex-wrap: wrap;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 4);
      }
      &-value {
        color: $on-surface;
      }
    }
    &-categories {
      list-style: none;
    }
    &-category {
      border-top: 1px solid $divider;
      display: grid;
      gap: 8px;
      padding: 16px 0;
      @include media-breakpoint-up(sm) {
        padding: 24px 0;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-top-width, 1);
        @include responsive-spacing(gap, 8);
        @include responsive-spacing(padding-bottom, 24);
        @include responsive-spacing(padding-top, 24);
      }
      &-title {
        color: $on-surface-variant-2;
      }
      &:first-child {
        border-top: unset;
        padding-top: 0;
      }
    }
    &-items {
      list-style: none;
    }
    &-item {
      padding: 9px 0;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-bottom, 9);
        @include responsive-spacing(padding-top, 9);
      }
    }
    &-bottom {
      background-color: $white;
      bottom: 0;
      padding: 4px 0;
      position: sticky;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-bottom, 4);
        @include responsive-spacing(padding-top, 4);
      }
      &:before {
        background: linear-gradient(
          180deg,
          rgba(255, 255, 255, 0) 0%,
          #ffffff 95%
        );
        content: '';
        height: 72px;
        position: absolute;
        top: -72px;
        width: 100%;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(height, 72);
          @include responsive-spacing(top, -72);
        }
      }
      &::after {
        background-color: $white;
        bottom: -4px;
        content: '';
        height: 8px;
        left: 0;
        position: relative;
        width: 100%;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(bottom, -4);
          @include responsive-spacing(height, 8);
        }
      }
    }
    &-button {
      margin: 0 auto;
    }
  }
}
</style>
